import React from "react";
import { StyleSheet, View } from "react-native";
import { WarningIcon } from "@phosphor-icons/react";
import type { GetModernizedCourseEvaluatorIssuesQuery } from "@axio/graphql/generated/graphql";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import { DIMENSION_ID_ALIASES, type GapAnalysisDimension } from "./gapAnalysisConstants";
import { parseGroupedFindings } from "../utils/gapAnalysisSummary";

export interface WorkloadData {
  creditHours?: number | null;
  lengthInWeeks?: number | null;
  modules: Array<{
    id: string;
    title: string;
    estimatedMinutes?: number | null;
    removalRecommended?: boolean | null;
  }>;
}

interface DimensionIssuePanelProps {
  dimension: GapAnalysisDimension;
  evaluatorIssues?: Array<
    NonNullable<NonNullable<GetModernizedCourseEvaluatorIssuesQuery["getModernizedCourse"]>["evaluatorIssues"]>[number]
  >;
  workloadData?: WorkloadData;
}

function normalizeIssueDimensionId(dimensionId: string): string {
  const normalized = dimensionId
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
  return DIMENSION_ID_ALIASES[normalized] ?? normalized;
}

function resolveDimensionIssues(
  dimension: GapAnalysisDimension,
  evaluatorIssues: NonNullable<
    NonNullable<GetModernizedCourseEvaluatorIssuesQuery["getModernizedCourse"]>["evaluatorIssues"]
  >
): GapAnalysisDimension {
  const allDimensionIssues = evaluatorIssues.filter(
    (issue) => normalizeIssueDimensionId(issue.dimension) === dimension.id
  );
  const filteredIssues = allDimensionIssues.slice(0, 4);

  const mappedDescriptions = filteredIssues
    .map((issue) => issue.description)
    .filter((description) => description.trim().length > 0);

  const issuesWithEntity = filteredIssues
    .filter((issue) => issue.description.trim().length > 0)
    .map((issue) => ({
      id: issue.id,
      description: issue.description,
      entityId: issue.entityId,
      entityType: issue.entityType,
      title: issue.title,
    }));

  return {
    ...dimension,
    issues: filteredIssues.length,
    issuesList: mappedDescriptions,
    issuesWithEntity,
  };
}

export function DimensionIssuePanel({ dimension, evaluatorIssues = [], workloadData }: DimensionIssuePanelProps) {
  const resolvedDimension = resolveDimensionIssues(dimension, evaluatorIssues);

  if (dimension.id === "cognitive-load") {
    return <CognitiveLoadIssuePanel dimension={resolvedDimension} workloadData={workloadData} />;
  }

  if (dimension.id === "rubric-quality") {
    return <RubricQualityIssuePanel dimension={resolvedDimension} />;
  }

  if (dimension.id === "clo-coverage" || dimension.id === "industry-alignment") {
    return <GenericIssuePanel dimension={resolvedDimension} />;
  }

  if (dimension.id === "accessibility") {
    return <GenericIssuePanel dimension={resolvedDimension} headerContent={<AccessibilityScopeNotice />} />;
  }

  return <GenericIssuePanel dimension={resolvedDimension} />;
}

// ──────────────────────────────────────────────────────────────────────────────
// Cognitive load panel — workload stats + module bars + findings
// ──────────────────────────────────────────────────────────────────────────────

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function CognitiveLoadIssuePanel({
  dimension,
  workloadData,
}: {
  dimension: GapAnalysisDimension;
  workloadData?: WorkloadData;
}) {
  const { high: highFindings } = parseGroupedFindings(dimension.detailedFindings);
  const shouldShowSummary = Boolean(dimension.summary) && highFindings.length === 0;

  return (
    <View style={styles.issuePanel}>
      {workloadData && <WorkloadInlineSection workloadData={workloadData} />}
      {shouldShowSummary && <NarrativeSummary summary={dimension.summary!} />}
      {highFindings.length > 0 ? <HighPriorityFindings findings={highFindings} /> : <IssueList dimension={dimension} />}
    </View>
  );
}

function WorkloadInlineSection({ workloadData }: { workloadData: WorkloadData }) {
  const { creditHours, lengthInWeeks, modules } = workloadData;

  const CARNEGIE_HOURS_PER_CREDIT = 45;
  const carnegieTargetHours = creditHours != null ? creditHours * CARNEGIE_HOURS_PER_CREDIT : null;
  // Term-adjusted weekly target; falls back to 15-week assumption when term length is unknown.
  const weeklyTargetMinutes =
    carnegieTargetHours != null && lengthInWeeks != null
      ? Math.round((carnegieTargetHours * 60) / lengthInWeeks)
      : carnegieTargetHours != null
        ? Math.round((carnegieTargetHours * 60) / 15) // 15-week assumption
        : null;
  const perModuleTargetMinutes =
    carnegieTargetHours != null && modules.length > 0 ? Math.round((carnegieTargetHours * 60) / modules.length) : null;
  const actualTotalMinutes = modules.reduce((sum, m) => sum + (m.estimatedMinutes ?? 0), 0);
  const actualTotalHours = Math.round((actualTotalMinutes / 60) * 10) / 10;
  const hasWorkloadData = modules.some((m) => m.estimatedMinutes != null);

  const currentWeeks =
    hasWorkloadData && weeklyTargetMinutes != null && weeklyTargetMinutes > 0
      ? Math.round((actualTotalMinutes / weeklyTargetMinutes) * 10) / 10
      : null;

  const weeksRatio =
    currentWeeks != null && lengthInWeeks != null && lengthInWeeks > 0 ? currentWeeks / lengthInWeeks : null;
  const weeksStatus =
    weeksRatio == null
      ? null
      : weeksRatio > 1.15
        ? { color: colors.error[600] ?? "#D92D20" }
        : weeksRatio < 0.85
          ? { color: colors.warning[600] ?? "#DC6803" }
          : { color: colors.success[600] ?? "#079455" };

  if (creditHours == null && lengthInWeeks == null && !hasWorkloadData) {
    return null;
  }

  return (
    <View style={wStyles.container}>
      <CustomText text="Workload" useTranslationText={false} size="xs" weight="bold" style={wStyles.sectionLabel} />

      <View style={wStyles.statStrip}>
        {creditHours != null && (
          <View style={wStyles.statChip}>
            <CustomText
              text={`${creditHours} cr`}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={wStyles.statValue}
            />
            <CustomText text="credits" useTranslationText={false} size="xs" style={wStyles.statLabel} />
          </View>
        )}
        {lengthInWeeks != null && (
          <View style={wStyles.statChip}>
            <CustomText
              text={`${lengthInWeeks} wk`}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={wStyles.statValue}
            />
            <CustomText text="target" useTranslationText={false} size="xs" style={wStyles.statLabel} />
          </View>
        )}
        {currentWeeks != null && (
          <View
            style={[
              wStyles.statChip,
              weeksStatus && {
                borderColor: `${weeksStatus.color}50`,
                backgroundColor: `${weeksStatus.color}0D`,
              },
            ]}>
            <CustomText
              text={`${currentWeeks} wk`}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={[wStyles.statValue, weeksStatus && { color: weeksStatus.color }]}
            />
            <CustomText
              text="current"
              useTranslationText={false}
              size="xs"
              style={[wStyles.statLabel, weeksStatus && { color: weeksStatus.color }]}
            />
          </View>
        )}
        {carnegieTargetHours != null && (
          <View style={wStyles.statChip}>
            <CustomText
              text={`~${carnegieTargetHours}h`}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={wStyles.statValue}
            />
            <CustomText text="target" useTranslationText={false} size="xs" style={wStyles.statLabel} />
          </View>
        )}
        {hasWorkloadData && (
          <View
            style={[
              wStyles.statChip,
              weeksStatus && {
                borderColor: `${weeksStatus.color}50`,
                backgroundColor: `${weeksStatus.color}0D`,
              },
            ]}>
            <CustomText
              text={`~${actualTotalHours}h`}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={[wStyles.statValue, weeksStatus && { color: weeksStatus.color }]}
            />
            <CustomText
              text="current"
              useTranslationText={false}
              size="xs"
              style={[wStyles.statLabel, weeksStatus && { color: weeksStatus.color }]}
            />
          </View>
        )}
        {perModuleTargetMinutes != null && (
          <View style={wStyles.statChip}>
            <CustomText
              text={`~${formatMinutes(perModuleTargetMinutes)}`}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={wStyles.statValue}
            />
            <CustomText text="per module" useTranslationText={false} size="xs" style={wStyles.statLabel} />
          </View>
        )}
      </View>

      {hasWorkloadData && perModuleTargetMinutes != null && (
        <View style={wStyles.moduleList}>
          {modules.map((mod) => {
            const min = mod.estimatedMinutes ?? 0;
            const r = perModuleTargetMinutes > 0 ? min / perModuleTargetMinutes : 0;
            const barColor =
              r < 0.5
                ? (colors.warning[400] ?? "#FDB022")
                : r > 1.4
                  ? (colors.error[400] ?? "#F97066")
                  : (colors.success[400] ?? "#47CD89");
            const cappedFill = Math.min(r / 1.5, 1) * 100;
            const isRemoval = mod.removalRecommended;

            return (
              <View key={mod.id} style={wStyles.moduleRow}>
                <CustomText
                  text={mod.title}
                  useTranslationText={false}
                  size="xs"
                  numberOfLines={1}
                  style={[wStyles.moduleTitle, isRemoval && wStyles.moduleTitleMuted]}
                />
                <View style={wStyles.barTrack}>
                  <View style={wStyles.targetLine} />
                  <View
                    style={[
                      wStyles.barFill,
                      {
                        width: `${cappedFill}%` as unknown as number,
                        backgroundColor: barColor,
                        opacity: isRemoval ? 0.4 : 1,
                      },
                    ]}
                  />
                </View>
                <CustomText
                  text={min > 0 ? formatMinutes(min) : "—"}
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={[wStyles.moduleTime, { color: isRemoval || min === 0 ? colors.grey[400] : colors.grey[700] }]}
                />
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Generic issue panel — handles summary, findings, issue list, and optional approval
// ──────────────────────────────────────────────────────────────────────────────

function GenericIssuePanel({
  dimension,
  headerContent,
}: {
  dimension: GapAnalysisDimension;
  headerContent?: React.ReactNode;
}) {
  const { high: highFindings } = parseGroupedFindings(dimension.detailedFindings);
  const shouldShowSummary = Boolean(dimension.summary) && highFindings.length === 0;

  return (
    <View style={styles.issuePanel}>
      {headerContent}
      {shouldShowSummary && <NarrativeSummary summary={dimension.summary!} />}
      {highFindings.length > 0 ? (
        <HighPriorityFindings findings={highFindings} />
      ) : shouldShowSummary && dimension.issues === 0 ? null : (
        <IssueList dimension={dimension} />
      )}
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Rubric quality panel — separate layout/styles
// ──────────────────────────────────────────────────────────────────────────────

function RubricQualityIssuePanel({ dimension }: { dimension: GapAnalysisDimension }) {
  const { high: highFindings } = parseGroupedFindings(dimension.detailedFindings);
  const shouldShowSummary = Boolean(dimension.summary) && highFindings.length === 0;
  const rubricIssues =
    dimension.issuesWithEntity && dimension.issuesWithEntity.length > 0
      ? dimension.issuesWithEntity.map((issue) => issue.description)
      : dimension.issuesList;
  const emptyStateMessage = getEmptyStateMessage(dimension);

  return (
    <View style={styles.rubricPanel}>
      {shouldShowSummary && <NarrativeSummary summary={dimension.summary!} />}
      {highFindings.length > 0 ? (
        <HighPriorityFindings findings={highFindings} />
      ) : (
        <View>
          <CustomText
            text={`${rubricIssues.length} ${rubricIssues.length === 1 ? "item" : "items"}`}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.rubricSectionTitle}
          />
          <View style={styles.rubricIssueList}>
            {emptyStateMessage ? (
              <View style={styles.emptyState}>
                <CustomText
                  text={emptyStateMessage}
                  useTranslationText={false}
                  size="s"
                  weight="normal"
                  style={styles.emptyStateText}
                />
              </View>
            ) : (
              rubricIssues.map((issue) => (
                <View key={issue} style={styles.rubricIssueItem}>
                  <CustomText
                    text={issue}
                    useTranslationText={false}
                    size="s"
                    weight="normal"
                    style={styles.rubricIssueText}
                  />
                </View>
              ))
            )}
          </View>
        </View>
      )}
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ──────────────────────────────────────────────────────────────────────────────

function AccessibilityScopeNotice() {
  return (
    <View style={styles.accessibilityScopeNotice}>
      <View style={styles.accessibilityScopeIcon}>
        <WarningIcon size={16} color={colors.warning[700]} weight="bold" />
      </View>
      <View style={styles.accessibilityScopeTextGroup}>
        <CustomText
          text="Limited scope - manual review still required."
          useTranslationText={false}
          size="xs"
          weight="bold"
          style={styles.accessibilityScopeTitle}
        />
        <CustomText
          text="Axio only checks activity titles and descriptions for HTML accessibility signals. Images, videos, slides, PDFs, and external multimedia are not evaluated - verify those manually before publishing."
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.accessibilityScopeText}
        />
      </View>
    </View>
  );
}

function IssueList({ dimension }: { dimension: GapAnalysisDimension }) {
  const emptyStateMessage = getEmptyStateMessage(dimension);

  return (
    <>
      <CustomText
        text={`${dimension.issues} ${dimension.issues === 1 ? "item" : "items"}`}
        useTranslationText={false}
        size="xs"
        weight="bold"
        style={styles.issueCount}
      />
      <View style={styles.issueList}>
        {emptyStateMessage ? (
          <View style={styles.emptyState}>
            <CustomText
              text={emptyStateMessage}
              useTranslationText={false}
              size="s"
              weight="normal"
              style={styles.emptyStateText}
            />
          </View>
        ) : dimension.issuesWithEntity && dimension.issuesWithEntity.length > 0 ? (
          dimension.issuesWithEntity.map((issue) => (
            <View key={issue.id} style={styles.issueItem}>
              <CustomText
                text={issue.description}
                useTranslationText={false}
                size="s"
                weight="normal"
                style={styles.issueText}
              />
            </View>
          ))
        ) : (
          dimension.issuesList.map((issue) => (
            <View key={issue} style={styles.issueItem}>
              <CustomText text={issue} useTranslationText={false} size="s" weight="normal" style={styles.issueText} />
            </View>
          ))
        )}
      </View>
    </>
  );
}

function getEmptyStateMessage(dimension: GapAnalysisDimension): string | null {
  const hasRenderableIssues = dimension.issuesList.length > 0 || (dimension.issuesWithEntity?.length ?? 0) > 0;

  if (hasRenderableIssues) {
    return null;
  }

  if (dimension.issues > 0) {
    return `No ${dimension.label} issue details are available for this run.`;
  }

  if (dimension.id === "content-freshness") {
    return "No content freshness issues found. Axio did not find stale links, outdated references, or stale source material in the Common Cartridge, ZIP upload, or LMS sync content reviewed in this run.";
  }

  return `No ${dimension.label} issues found in this run.`;
}

function NarrativeSummary({ summary }: { summary: string }) {
  return (
    <View style={styles.narrativeSummary}>
      <CustomText
        text={summary}
        useTranslationText={false}
        size="s"
        weight="normal"
        style={styles.narrativeSummaryText}
      />
    </View>
  );
}

function HighPriorityFindings({ findings }: { findings: string[] }) {
  return (
    <View style={styles.detailedFindingsSection}>
      <CustomText text="Priority items" useTranslationText={false} size="xs" weight="bold" style={styles.issueCount} />
      <View style={styles.findingList}>
        {findings.map((finding) => (
          <View key={finding} style={[styles.findingItem, { borderLeftColor: colors.error[200] }]}>
            <View style={styles.findingBullet} />
            <CustomText
              text={finding}
              useTranslationText={false}
              size="s"
              weight="normal"
              style={styles.findingDescription}
            />
          </View>
        ))}
      </View>
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Styles — workload inline section
// ──────────────────────────────────────────────────────────────────────────────

const wStyles = StyleSheet.create({
  container: {
    marginBottom: 20,
    gap: 10,
  },
  sectionLabel: {
    color: colors.grey[400],
    fontSize: 10,
    lineHeight: 14,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  statStrip: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  statChip: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignItems: "center",
    backgroundColor: colors.grey[50],
  },
  statValue: {
    color: colors.grey[900],
    fontSize: 12,
    lineHeight: 16,
  },
  statLabel: {
    color: colors.grey[500],
    fontSize: 10,
    lineHeight: 14,
  },
  moduleList: {
    gap: 5,
  },
  moduleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  moduleTitle: {
    color: colors.grey[600],
    width: 120,
    flexShrink: 0,
    fontSize: 11,
    lineHeight: 16,
  },
  moduleTitleMuted: {
    color: colors.grey[400],
  },
  barTrack: {
    flex: 1,
    height: 5,
    backgroundColor: colors.grey[100],
    borderRadius: 3,
    overflow: "hidden",
    position: "relative",
  },
  targetLine: {
    position: "absolute",
    right: "33%",
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: colors.grey[300],
    zIndex: 1,
  },
  barFill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 3,
  },
  moduleTime: {
    width: 40,
    textAlign: "right",
    flexShrink: 0,
    fontSize: 11,
    lineHeight: 16,
  },
});

// ──────────────────────────────────────────────────────────────────────────────
// Styles — shared panels
// ──────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  accessibilityScopeNotice: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.warning[300],
    backgroundColor: colors.warning[25],
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  accessibilityScopeIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  accessibilityScopeTextGroup: {
    flex: 1,
    gap: 4,
  },
  accessibilityScopeTitle: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  accessibilityScopeText: {
    color: colors.grey[700],
    lineHeight: 18,
  },
  issuePanel: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    backgroundColor: colors.grey[25],
    paddingHorizontal: 24,
    paddingVertical: 22,
  },
  issueCount: {
    color: colors.grey[500],
    textTransform: "uppercase",
    marginBottom: 16,
  },
  issueList: {
    gap: 14,
  },
  issueItem: {
    borderLeftWidth: 2,
    borderLeftColor: colors.grey[200],
    paddingLeft: 16,
    gap: 0,
  },
  issueText: {
    color: colors.grey[900],
    lineHeight: 22,
  },
  emptyState: {
    borderLeftWidth: 2,
    borderLeftColor: colors.success[300],
    paddingLeft: 16,
  },
  emptyStateText: {
    color: colors.grey[800],
    lineHeight: 22,
  },
  rubricPanel: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    backgroundColor: colors.grey[25],
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  rubricSectionTitle: {
    color: colors.grey[500],
    fontSize: 11,
    lineHeight: 16,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 12,
  },
  rubricIssueList: {
    gap: 10,
  },
  rubricIssueItem: {
    borderLeftWidth: 2,
    borderLeftColor: colors.grey[200],
    paddingLeft: 12,
    overflow: "hidden",
  },
  rubricIssueText: {
    color: colors.grey[900],
    lineHeight: 21,
  },
  narrativeSummary: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 6,
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  narrativeSummaryText: {
    color: colors.grey[800],
    lineHeight: 22,
  },
  detailedFindingsSection: {
    gap: 16,
  },
  findingList: {
    gap: 10,
  },
  findingItem: {
    borderLeftWidth: 2,
    paddingLeft: 12,
    gap: 8,
    flexDirection: "row",
    alignItems: "flex-start",
    borderRadius: 4,
    paddingVertical: 2,
  },
  findingBullet: {
    width: 5,
    height: 5,
    borderRadius: 99,
    backgroundColor: colors.error[500],
    marginTop: 8,
    flexShrink: 0,
  },
  findingDescription: {
    color: colors.grey[800],
    lineHeight: 22,
  },
});
